import * as React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../app/ApplicationContext";
import {ErrorAlert} from "../common/ErrorAlert";
import {ErrorResponse, FinalizeItemAlertsRequest, ItemAlert, PageResponseEnvelope,} from "../api/dtos/dtos";
import {ColumnProps, TableRowSelection} from "antd/lib/table";
import * as tableUtils from "../utils/table-utils";
import {filterableColumn, GridState, searchableSortableColumn, sortableColumn} from "../utils/table-utils";
import locales from "../locales";
import {Button, Table} from "antd";
import furoApi from "../api/FuroApi";
import * as routerUtils from "../utils/router-utils";
import {formatDate} from "../utils/date-utils";
import {AlertState, getAlertStateFilters, renderAlertStateCondensed} from "./state";
import ButtonGroup from "antd/lib/button/button-group";
import {editAlertRequest, ItemAlertEditor} from "./ItemAlertEditor";
import {Link} from "react-router-dom";
import * as paths from "../paths";
import {showDeleteConfirm} from "../utils/dialogs";
import {CheckboxProps} from "antd/lib/checkbox";
import {VisibilityProperty} from "csstype";
import ButtonBar from "../common/ButtonBar";
import HandleAlertsModal from "./HandleAlertsModal";
import {renderUnitColumn} from "../utils/number-utils";
import {renderItemName} from "../item/item-name-renderer";
import {
  AlertController,
  bindAlertController,
  GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";

interface State extends GridState<ItemAlert> {
  error?: ErrorResponse;
  editedAlert?: ItemAlert;
  selectedItems: ItemAlert[];
  handledItems: ItemAlert[];
}

const STATE_KEY: string = 'state';

const initialGridState = (): GridState<ItemAlert> => {
  return {
    ...tableUtils.initialGridState(),
    filters: {
      [STATE_KEY]: [AlertState.NEW, AlertState.TRIGGERED]
    }
  }
};

class AlertsPage extends React.Component<RouteComponentProps, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly alertController?: AlertController;

  public state: State = {
    selectedItems: [],
    handledItems: [],

    // Default initial grid state
    ...initialGridState(),

    // Override with cached state from Router
    ...this.props.location.state
  };

  public componentDidMount() {
    this.reload();
  }

  public render(): React.ReactNode {
    const {error, editedAlert, selectedItems, handledItems} = this.state;

    const columns: Array<ColumnProps<ItemAlert>> = [
      {
        ...filterableColumn('', STATE_KEY, this.state, r => renderAlertStateCondensed(r.state), getAlertStateFilters(), true),
        width: 38,
      },
      sortableColumn(locales.alert_date, 'alertDate', this.state, r => formatDate(r.alertDate)),
      searchableSortableColumn(locales.item, 'item', this.state, this.renderItemCell),
      searchableSortableColumn(locales.inventoryNumber, 'inventoryNumber', this.state, (record) => record.item.inventoryNumber),
      searchableSortableColumn(locales.appellation, 'name', this.state, r => r.name),
      sortableColumn(locales.operation_hours_reading, 'operationHours', this.state, this.renderOperationHoursCell),
      sortableColumn(locales.alert_handled_date, 'handledDate', this.state, this.renderHandledDateCell),
      {key: 'actions', width: '80px', render: this.renderActionCell},
    ];

    const handleButtonVisibility: VisibilityProperty = selectedItems.length > 0 ? 'visible' : 'hidden';

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <GlobalAlertContextConsumer>
          {(state) => bindAlertController(state.item, this)}
        </GlobalAlertContextConsumer>

        {editedAlert && (
          <ItemAlertEditor
            alert={editAlertRequest(editedAlert)}
            alertId={editedAlert.id}
            onAlertSaved={this.onAlertsChanged}
            onAlertCanceled={this.onAlertCanceled}
          />
        )}

        {handledItems && (
          <HandleAlertsModal
            alerts={handledItems}
            onCancel={this.clearHandledItems}
            onSubmit={this.onHandleConfirmed}
          />
        )}

        <ButtonBar style={{marginBottom: 8}}>
          <Button
            type="primary"
            size="small"
            icon="lock"
            style={{visibility: handleButtonVisibility}}
            onClick={this.onHandleButtonClick}
          >
            {locales.handle}
          </Button>
        </ButtonBar>

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          rowSelection={this.rowSelection()}
          scroll={{x: 1024}}
        />

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private renderOperationHoursCell = (record: ItemAlert) => {
    return renderUnitColumn(record.operationHours, locales.hours)
  };

  private renderHandledDateCell = (record: ItemAlert) => {
    return record.handledDate ? formatDate(record.handledDate) : '';
  };

  private renderItemCell = (record: ItemAlert) => {
    return <Link to={paths.viewItemPath(record.item.id)}>{renderItemName(record.item)}</Link>;
  };

  private renderActionCell = (text: any, record: ItemAlert) => {
    const onEditClick = () => this.onEditAlertClick(record);
    const onDeleteClick = () => this.onDeleteAlertClick(record);
    return (
      <div className="action-column" style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
        <ButtonGroup>
          <Button icon="edit" onClick={onEditClick} title={locales.edit}/>
          <Button icon="delete" onClick={onDeleteClick} title={locales.delete}/>
        </ButtonGroup>
      </div>
    );
  };

  private rowSelection = (): TableRowSelection<ItemAlert> => {
    const selectedRowKeys = this.state.selectedItems.map((record) => tableUtils.idRowKeyFn(record));
    return {
      selectedRowKeys,
      onChange: (selectedKeys: string[], selectedItems: ItemAlert[]) => {
        this.setState({selectedItems});
      },
      getCheckboxProps: (record: ItemAlert): CheckboxProps => ({
        disabled: record.handledDate !== null,
      }),
    };
  };

  private onGridChange = (newState: GridState<ItemAlert>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const request = tableUtils.toGridRequest(this.state);
    const promise = furoApi.alerts.getAll(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<ItemAlert>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };

  private onHandleButtonClick = () => {
    this.setState({handledItems: this.state.selectedItems});
  };

  private clearHandledItems = () => {
    this.setState({handledItems: []});
  };

  private clearSelection = () => {
    this.setState({selectedItems: []});
  };

  private onHandleConfirmed = (request: FinalizeItemAlertsRequest) => {
    const promise = furoApi.alerts.finalize(request)
      .then(this.clearHandledItems)
      .then(this.clearSelection)
      .then(this.onAlertsChanged)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onEditAlertClick = (alert: ItemAlert) => {
    this.setState({editedAlert: alert})
  };

  private onDeleteAlertClick = (alert: ItemAlert) => {
    const deleteAlert = () => {
      const promise = furoApi.alerts.delete(alert.id)
        .then(this.onAlertsChanged)
        .catch(this.onApiError);
      withLoadingOverlay(promise, this.loadingHandler);
    };
    showDeleteConfirm(deleteAlert);
  };

  private onAlertsChanged = () => {
    this.reload();
    this.alertController?.refreshAlertCount();
    this.onAlertCanceled();
  };

  private onAlertCanceled = () => {
    this.setState({editedAlert: undefined});
  };
}

export default withRouter(AlertsPage);



// WEBPACK FOOTER //
// ./src/alerts/AlertsPage.tsx